import Cookies from 'js-cookie';

import { hasMarketingConsent } from '@/lib/consent/marketing-consent';

const FBC_COOKIE = '_fbc';
const FBC_MAX_AGE_DAYS = 90;

// Meta: 1 for a registrable domain (ihrhelden.de), 2 for a host with www.
const subdomainIndex = (): number =>
  window.location.hostname.startsWith('www.') ? 2 : 1;

// The pixel scopes `_fbc` to the registrable domain; a host-only cookie would sit beside it as a
// second `_fbc`. No domain on localhost or an IP, where a domain attribute is rejected.
const cookieDomain = (): string | undefined => {
  const host = window.location.hostname.replace(/^www\./, '');
  return host.includes('.') && !/^[\d.]+$/.test(host) ? host : undefined;
};

// `fb.<index>.<ms>.<fbclid>[.<appendix>]`: the pixel appends a suffix, so the click ID is segment 4.
const fbclidOf = (fbc: string): string => fbc.split('.')[3] ?? '';

// A deal opened from an ad and sent to /login carries its fbclid inside `?redirect=`.
const fbclidFromLocation = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const direct = params.get('fbclid');
  if (direct) return direct;

  const redirect = params.get('redirect');
  if (!redirect) return null;
  try {
    return new URL(redirect, window.location.origin).searchParams.get('fbclid');
  } catch {
    return null;
  }
};

/**
 * Meta click ID for CAPI events. Meta's pixel only writes `_fbc` once it has loaded, which
 * needs consent and GTM, so a visitor who registers first would send no click ID although
 * `fbclid` is in the URL. Rebuilds it as `fb.<index>.<ms>.<fbclid>` (Meta's documented format,
 * fbclid kept case-sensitive) and keeps it for the later signup page. Like Meta asks, it also
 * replaces a cookie whose fbclid differs from the one in the URL (a newer ad click). Never
 * builds or stores anything without marketing consent.
 */
export const getFbc = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  const existing = Cookies.get(FBC_COOKIE) || undefined;
  if (!hasMarketingConsent()) return existing;

  const fbclid = fbclidFromLocation();
  if (!fbclid) return existing;
  if (existing && fbclidOf(existing) === fbclid) return existing;

  const fbc = `fb.${subdomainIndex()}.${Date.now()}.${fbclid}`;
  Cookies.set(FBC_COOKIE, fbc, {
    expires: FBC_MAX_AGE_DAYS,
    path: '/',
    domain: cookieDomain(),
    sameSite: 'lax',
    secure: window.location.protocol === 'https:'
  });
  return fbc;
};
